"use client"

import React from "react"
import { Search, Bell, Settings, User, Menu } from "lucide-react"
import { useLocation } from "react-router-dom"
import { Button } from "./ui/button"
import { Input } from "./ui/input"
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar"
import { useAuth } from "../hooks/useAuth"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu"
import { Badge } from "./ui/badge"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "./ui/breadcrumb"

// Função para gerar breadcrumbs baseado na rota atual
const generateBreadcrumbs = (pathname) => {
  const paths = pathname.split('/').filter(Boolean)
  
  const breadcrumbMap = {
    '': 'Home',
    'eventos': 'Eventos',
    'novo': 'Novo Evento',
    'editar': 'Editar Evento',
    'disponibilidade': 'Disponibilidade',
    'configuracoes': 'Configurações',
    'logs': 'Logs',
    'relatorios': 'Relatórios'
  }

  const breadcrumbs = []
  
  // Adiciona Home sempre
  breadcrumbs.push({ title: 'Home', href: '/', isLast: false })
  
  // Processa os caminhos
  let currentPath = ''
  paths.forEach((path, index) => {
    currentPath += `/${path}`
    const title = breadcrumbMap[path] || path.charAt(0).toUpperCase() + path.slice(1)
    const isLast = index === paths.length - 1
    
    breadcrumbs.push({
      title,
      href: currentPath,
      isLast
    })
  })

  return breadcrumbs
}

export function Header({ onToggleSidebar, isMobile }) {
  const location = useLocation()
  const { user, logout } = useAuth()
  const breadcrumbs = generateBreadcrumbs(location.pathname)

  const handleLogout = () => {
    logout()
  }

  return (
    <header className="sticky top-0 z-30 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="flex h-14 sm:h-16 items-center px-3 sm:px-4 gap-2 sm:gap-4">
        
        {/* Mobile Sidebar Toggle - Sempre visível em telas pequenas */}
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 sm:h-9 sm:w-9 flex md:hidden"
          onClick={onToggleSidebar}
        >
          <Menu className="h-4 w-4" />
          <span className="sr-only">Toggle sidebar</span>
        </Button>

        {/* Breadcrumb - Hidden on mobile */}
        <Breadcrumb className="hidden md:flex">
          <BreadcrumbList>
            {breadcrumbs.map((crumb, index) => (
              <React.Fragment key={crumb.href}>
                <BreadcrumbItem>
                  {crumb.isLast ? (
                    <BreadcrumbPage className="text-sm">{crumb.title}</BreadcrumbPage>
                  ) : (
                    <BreadcrumbLink href={crumb.href} className="text-sm">{crumb.title}</BreadcrumbLink>
                  )}
                </BreadcrumbItem>
                {index < breadcrumbs.length - 1 && <BreadcrumbSeparator />}
              </React.Fragment>
            ))}
          </BreadcrumbList>
        </Breadcrumb>

        {/* Mobile Page Title */}
        {isMobile && (
          <div className="flex-1 md:hidden">
            <h1 className="text-lg font-semibold truncate">
              {breadcrumbs[breadcrumbs.length - 1]?.title || 'RAS-5ºCPA'}
            </h1>
          </div>
        )}

        {/* Search Bar - Desktop only */}
        <div className="hidden md:flex flex-1 justify-center">
          <div className="relative w-full max-w-md">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Pesquisar..."
              className="pl-10 pr-4 h-9"
            />
          </div>
        </div>

        {/* Spacer for desktop */}
        {!isMobile && <div className="flex-1 md:hidden"></div>}

        {/* Right Actions */}
        <div className="flex items-center gap-1 sm:gap-2">
          
          {/* Mobile Search Button */}
          {isMobile && (
            <Button variant="ghost" size="icon" className="h-8 w-8 md:hidden">
              <Search className="h-4 w-4" />
              <span className="sr-only">Pesquisar</span>
            </Button>
          )}
          
          {/* Notifications */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-9 sm:w-9 relative">
                <Bell className="h-4 w-4" />
                <Badge className="absolute -top-1 -right-1 h-4 w-4 sm:h-5 sm:w-5 flex items-center justify-center text-xs p-0">
                  3
                </Badge>
                <span className="sr-only">Notificações</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72 sm:w-80">
              <DropdownMenuLabel>Notificações</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem>
                <div className="flex flex-col gap-1">
                  <p className="text-sm font-medium">Nova solicitação de vaga</p>
                  <p className="text-xs text-muted-foreground">
                    Recebida há 5 minutos
                  </p>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem>
                <div className="flex flex-col gap-1">
                  <p className="text-sm font-medium">Evento atualizado</p>
                  <p className="text-xs text-muted-foreground">
                    Recebida há 1 hora
                  </p>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem>
                <div className="flex flex-col gap-1">
                  <p className="text-sm font-medium">Sistema atualizado</p>
                  <p className="text-xs text-muted-foreground">
                    Recebida há 2 horas
                  </p>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Settings - Hidden on small mobile */}
          <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-9 sm:w-9 hidden xs:flex">
            <Settings className="h-4 w-4" />
            <span className="sr-only">Configurações</span>
          </Button>

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 sm:h-9 sm:w-9 rounded-full">
                <Avatar className="h-7 w-7 sm:h-8 sm:w-8">
                  <AvatarImage src="https://github.com/shadcn.png" alt="Avatar" />
                  <AvatarFallback>
                    <User className="h-4 w-4" />
                  </AvatarFallback>
                </Avatar>
                <span className="sr-only">Menu do usuário</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium truncate">{user?.nome || 'Usuário'}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {user?.email || 'usuario@ras.com'}
                  </p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                <DropdownMenuItem>
                  <User className="mr-2 h-4 w-4" />
                  <span>Perfil</span>
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <Settings className="mr-2 h-4 w-4" />
                  <span>Configurações</span>
                </DropdownMenuItem>
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout}>
                <span>Sair</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  )
}
